/**
 * AudioLabPanel.jsx
 * ===========================================================================
 * The Audio Lab — `docs/journal/AUDIO_LAB_AND_MASTERING_SPEC.md`.
 *
 * The Ingest Lab shows what the machine made of the TEXT and lets the BC correct it. Until
 * now there has been no equivalent for the AUDIO, so mastering and ACX-readiness stayed
 * invisible until somebody listened to a finished file. This is that panel.
 *
 * THE RULE THIS PANEL IS BUILT ON — the most transferable thing the ingest work produced:
 *
 *     Show consequences, not classifications. Never report a count for something you
 *     then discard.
 *
 * So it renders MEASURED NUMBERS next to the range they were judged against, never a bare
 * green tick. The Ingest Lab colour-codes classifications and still could not have caught the
 * worst ingest bug of that session, because the classification was CORRECT and the damage
 * happened downstream. A BC can argue with a threshold; nobody can argue with a tick.
 *
 * The thresholds themselves come from the SERVER (`acxTargets`) rather than being duplicated
 * here, so this panel cannot end up quoting limits the checker no longer uses.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CollapsibleSection from "../../../components/voiceRendering/CollapsibleSection";
// ⛔ In-app modal, never window.confirm — the no-native-dialogs rule.
import ConfirmModal from "../../../components/prototype/ConfirmModal";
import { useDispatch } from "react-redux";
import {
  Button, Dropdown, Header, Icon, Label, Message, Popup, Segment, Table,
} from "semantic-ui-react";

import {
  fetchAudioLab, masterUnit, pollMasteringProgress, abortMastering,
  resignRenderedChapterUrl,
} from "../../../slices/voiceRenderingSlice";

// Fast enough that a chapter's verdict appears within a second or two of landing, slow enough
// not to hammer a server that is simultaneously running ffmpeg.
const POLL_MS = 1500;

// --------------------------------------------------------------------------- formatting

const fmtDuration = (ms) => {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(sec).padStart(2, "0")}s`;
};

const fmtBytes = (b) => {
  if (!b) return "—";
  if (b > 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b > 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
};

/** Measured values with their unit — the whole point of the panel. */
function fmtValue(check) {
  if (check.value == null) return "not measured";
  if (check.unit === "ms") return `${(check.value / 1000).toFixed(2)} s`;
  if (check.unit === "dB") return `${check.value.toFixed(1)} dB`;
  if (check.unit === "Hz") return `${(check.value / 1000).toFixed(1)} kHz`;
  return `${check.value} ${check.unit}`;
}

/** The range a value was judged against, so the number means something. */
function fmtTarget(check) {
  const u = check.unit === "ms" ? "s" : check.unit;
  const scale = (v) => (check.unit === "ms" ? (v / 1000).toFixed(1) : v);
  if (check.equals != null) return `= ${scale(check.equals)} ${u}`;
  if (check.min != null && check.max != null) return `${scale(check.min)} … ${scale(check.max)} ${u}`;
  if (check.max != null) return `≤ ${scale(check.max)} ${u}`;
  if (check.min != null) return `≥ ${scale(check.min)} ${u}`;
  return "—";
}

// `incomplete` is deliberately its own colour. A chapter we could not measure is not a
// chapter that passed, and showing them alike is how an unshippable file ships.
const VERDICT = {
  pass: { color: "green", icon: "check circle", text: "ACX clean" },
  fail: { color: "red", icon: "times circle", text: "Fails ACX" },
  incomplete: { color: "yellow", icon: "question circle", text: "Not fully measured" },
};

// --------------------------------------------------------------------------- component

/**
 * @param {object}   props
 * @param {string}   props.bookId
 * @param {string}   props.selector      the unit to report on — see the `book.body` warning below
 * @param {string}   [props.unitLabel]   the unit's human name, for headings
 * @param {*}        [props.refreshSignal] ⭐ any value the PARENT changes when something has produced
 *   new audio outside this panel — in practice a completed stitch. Joins `load`'s deps; see the note
 *   there. Mastering needs no signal: this panel owns that job and reloads when it ends.
 */
export default function AudioLabPanel({ bookId, selector, unitLabel, refreshSignal }) {
  const dispatch = useDispatch();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [presetKey, setPresetKey] = useState(null);
  const [job, setJob] = useState(null);          // live mastering job snapshot
  const pollRef = useRef(null);

  // A job is only "running" while the server says so — never inferred from a local flag that
  // a failed poll or a page state change could leave stuck on.
  const mastering = job?.status === "running";

  const [openChapter, setOpenChapter] = useState(null);

  // Two elements, both playing, one muted — see loadPair.
  const rawRef = useRef(null);
  const masteredRef = useRef(null);
  const [pair, setPair] = useState(null);         // the loaded comparison
  const [side, setSide] = useState("mastered");   // which one you are hearing
  const [levelMatch, setLevelMatch] = useState(true);
  const [playing, setPlaying] = useState(false);
  // "Force all" re-encodes every chapter, so it asks first. See the note at the button.
  const [confirmForce, setConfirmForce] = useState(false);

  /**
   * ⛔ `refreshSignal` EXISTS BECAUSE THIS PANEL WENT STALE THE MOMENT AUDIO WAS PRODUCED.
   *
   * The deps were `[dispatch, bookId, selector]`, and neither changes while a STITCH runs — so a
   * finished 44-chapter stitch left the panel showing the answer it got beforehand: *"No stitched
   * data…"*. On 2026-10-01 that was read as a stitch still running or aborted, and nearly caused a
   * completed 44-chapter job to be re-run.
   *
   * ⚠️ MASTERING was never the problem — `startPolling` already awaits `load()` on a terminal job.
   * The gap is that the stitch job lives in `RenderingStudio`, and this panel cannot see it. So the
   * parent passes a value that CHANGES when a stitch completes, and it joins the deps here.
   *
   * ⭐ The irony worth remembering: the route's own comment says the Audio Lab is *"deliberately a
   * READ of `:RenderedChapter` rather than the in-memory stitch job, which dies with a server restart
   * and makes a completed stitch look like it never happened."* It was built to prevent exactly this
   * illusion and reproduced it one layer up.
   */
  const load = useCallback(async () => {
    if (!bookId || !selector) return;
    setLoading(true);
    setError(null);
    try {
      const r = await dispatch(fetchAudioLab({ bookId, selector })).unwrap();
      setReport(r);
      setPresetKey((k) => k || r.defaultPreset);
    } catch (e) {
      setError(e?.message || "Could not read the Audio Lab report");
    } finally {
      setLoading(false);
    }
  }, [dispatch, bookId, selector, refreshSignal]);

  useEffect(() => { load(); }, [load]);

  // A scope change invalidates everything on screen — the numbers belong to the old unit.
  useEffect(() => {
    setJob(null);
    setOpenChapter(null);
    setPair(null);
    setPlaying(false);
    rawRef.current?.pause();
    masteredRef.current?.pause();
  }, [selector, bookId]);

  // Stop polling on unmount so a navigate-away does not leave a timer running.
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const startPolling = useCallback((jobId) => {
    clearTimeout(pollRef.current);
    const tick = async () => {
      try {
        const snap = await dispatch(pollMasteringProgress(jobId)).unwrap();
        setJob(snap);
        if (snap.status === "running") {
          pollRef.current = setTimeout(tick, POLL_MS);
        } else {
          // Terminal: refresh the durable report so the table reflects what actually landed.
          await load();
        }
      } catch (e) {
        // A lost job snapshot is not lost work — the chapters are persisted as they complete,
        // so fall back to the durable report rather than reporting a failure that did not
        // necessarily happen.
        setJob(null);
        await load();
      }
    };
    pollRef.current = setTimeout(tick, 400);
  }, [dispatch, load]);

  /**
   * ⛔ TWO FLAGS, AND THEY ARE NOT INTERCHANGEABLE — see the note on the `masterUnit` thunk.
   *   `force` → run past the self-halt that stops a job after consecutive failures.
   *   `redo`  → master again even though every chapter already looks current.
   * "Run anyway" wants force. "Force all" wants redo. Passing force for both is why the latter
   * reported "nothing to do — 44 chapter(s) already mastered" and did nothing.
   */
  const runMastering = useCallback(async ({ force = false, redo = false } = {}) => {
    if (!bookId) return;
    setError(null);
    try {
      const snap = await dispatch(
        masterUnit({ bookId, selector, presetKey, unitLabel, force, redo })
      ).unwrap();
      setJob(snap);
      startPolling(snap.jobId);
    } catch (e) {
      setError(e?.message || "Mastering failed");
    }
  }, [dispatch, bookId, selector, presetKey, unitLabel, startPolling]);

  const stopMastering = useCallback(async () => {
    if (!job?.jobId) return;
    try {
      setJob(await dispatch(abortMastering(job.jobId)).unwrap());
    } catch (e) {
      setError(e?.message || "Could not stop the run");
    }
  }, [dispatch, job]);

  /**
   * A/B ONE CHAPTER — a real comparator, not two separate playbacks.
   *
   * Both files are loaded into their own <audio> element and played SIMULTANEOUSLY, with one
   * muted. Switching flips which is audible, so the change is instantaneous and lands on the
   * same word. Restarting the other file from zero — the obvious implementation — is close to
   * useless for this: by the time B reaches the passage being judged, the memory of A is gone.
   *
   * TWO CORRECTIONS MAKE THE COMPARISON HONEST:
   *
   *  1. TIME ALIGNMENT. Mastering cut the original head silence and substituted its own, so
   *     the same timestamp in the two files is NOT the same words. `contentOffsetMs` maps
   *     between them. Without it the difference you hear is alignment, not mastering.
   *
   *  2. LEVEL MATCHING. Mastering changes loudness on purpose, and louder reliably sounds
   *     better — so an unmatched A/B mostly measures the gain. Matching uses the MEASURED
   *     before/after RMS rather than the requested gain, because the limiter and the room-tone
   *     edit both move the result away from what was asked for. Only attenuation is applied
   *     (HTML volume cannot exceed 1), so the louder side is brought down to the quieter one.
   */
  const loadPair = useCallback(async (chapter) => {
    const raw = chapter.raw;
    const mastered = chapter.mastered;
    if (!raw?.uuid) return null;

    const sign = async (uuid, fallback) => {
      if (!uuid) return null;
      try {
        const r = await dispatch(resignRenderedChapterUrl(uuid)).unwrap();
        return r?.signedUrl || fallback || null;
      } catch (_) {
        return fallback || null;
      }
    };

    const [rawUrl, masteredUrl] = await Promise.all([
      sign(raw.uuid, raw.signedUrl),
      sign(mastered?.uuid, mastered?.signedUrl),
    ]);
    if (!rawUrl) return null;

    // Measured delta first; fall back to the requested gain when an older row predates the
    // measurement being stored. Zero means "cannot match" — stated, not silently assumed.
    const deltaDb = (mastered?.afterRmsDb != null && mastered?.beforeRmsDb != null)
      ? mastered.afterRmsDb - mastered.beforeRmsDb
      : (mastered?.appliedGainDb ?? 0);

    return {
      chapterNumber: chapter.chapterNumber,
      rawUrl,
      masteredUrl,
      offsetMs: mastered?.contentOffsetMs ?? 0,
      deltaDb,
      // Attenuate whichever side is louder.
      rawVolume: deltaDb < 0 ? Math.pow(10, deltaDb / 20) : 1,
      masteredVolume: deltaDb > 0 ? Math.pow(10, -deltaDb / 20) : 1,
    };
  }, [dispatch]);

  /**
   * Raw time → mastered time. Mastered t=0 sits at raw t=offsetMs, because mastering cut the
   * original head silence and substituted its own.
   */
  const masteredTimeFor = useCallback(
    (rawTime) => Math.max(0, rawTime - (pair?.offsetMs ?? 0) / 1000),
    [pair]
  );

  const startCompare = useCallback(async (chapter) => {
    setError(null);
    setPlaying(false);
    const p = await loadPair(chapter);
    if (!p) {
      setError(`Chapter ${chapter.chapterNumber} has no playable audio yet.`);
      return;
    }
    setPair(p);
    setSide(p.masteredUrl ? "mastered" : "raw");
  }, [loadPair]);

  /**
   * Flip sides. Nothing is seeked and nothing is started — the raw element is always the
   * clock and the mastered one is always running alongside it, so this is purely a change of
   * which stream reaches the ears. That is what makes the switch instantaneous.
   */
  const flipSide = useCallback((next) => {
    if (!pair?.masteredUrl) return;
    setSide(next);
  }, [pair]);

  /**
   * Keep the mastered element locked to the raw one.
   *
   * THE RAW ELEMENT'S TRANSPORT IS THE ONLY SOURCE OF TRUTH. It is the visible one with
   * native controls, so it is what actually gets pressed — and driving playback from a
   * separate custom button left the mastered element paused, which made switching to B play
   * silence rather than a difference. Every transport event on A is mirrored onto B here, so
   * however playback starts, both are running before either can be un-muted.
   */
  const syncB = useCallback((shouldPlay) => {
    const a = rawRef.current;
    const b = masteredRef.current;
    if (!a || !b || !pair?.masteredUrl) return;

    const want = masteredTimeFor(a.currentTime);
    // Only correct real drift; nudging every tick would stutter the muted stream.
    if (Math.abs(b.currentTime - want) > 0.15) b.currentTime = want;

    if (shouldPlay && b.paused) b.play().catch(() => { /* muted side; not worth surfacing */ });
    if (!shouldPlay && !b.paused) b.pause();
  }, [pair, masteredTimeFor]);

  const togglePlay = useCallback(async () => {
    const a = rawRef.current;
    if (!a) return;
    try {
      // Touches A only — A's own events start and stop B, so there is one path either way.
      if (a.paused) await a.play(); else a.pause();
    } catch (e) {
      setError(`Playback failed: ${e?.message || "unknown error"}`);
    }
  }, []);

  // Mute and level-match. Re-applied whenever the side or the toggle changes so the audible
  // stream is always the one the buttons claim.
  useEffect(() => {
    const a = rawRef.current;
    const b = masteredRef.current;
    if (!a) return;
    const hasB = !!pair?.masteredUrl;
    a.muted = hasB && side !== "raw";
    a.volume = levelMatch ? (pair?.rawVolume ?? 1) : 1;
    if (b) {
      b.muted = side !== "mastered";
      b.volume = levelMatch ? (pair?.masteredVolume ?? 1) : 1;
    }
  }, [side, levelMatch, pair]);

  const presetOptions = useMemo(
    () => (report?.presets || []).map((p) => ({
      key: p.key,
      value: p.key,
      text: p.label,
      description: p.acxCompliant ? undefined : "reference only",
    })),
    [report]
  );

  const activePreset = useMemo(
    () => (report?.presets || []).find((p) => p.key === presetKey) || null,
    [report, presetKey]
  );

  const totals = report?.totals;

  /**
   * The durable report, with any live job results laid over it.
   *
   * Without this the table would show nothing new until the run ended and the report
   * reloaded — which is exactly the failure being fixed: a nineteen-chapter run that reveals
   * its first verdict only once all nineteen are done.
   */
  const chapters = useMemo(() => {
    const base = report?.chapters || [];
    const running = job?.status === "running";
    const live = new Map((job?.chapters || []).map((c) => [c.chapterNumber, c]));

    return base.map((c) => {
      const l = live.get(c.chapterNumber);

      if (l) {
        return {
          ...c,
          liveState: "done",
          mastered: {
            ...(c.mastered || {}),
            uuid: l.uuid,
            s3Key: l.s3Key,
            masteringPreset: job.preset.key,
            appliedGainDb: l.gainDb,
            verdict: l.verdict,
            compliance: { verdict: l.verdict, checks: l.checks || [] },
          },
        };
      }

      // NOT YET RE-PROCESSED IN THIS RUN. Its verdict belongs to the PREVIOUS run, and
      // showing it plainly makes a stale failure look like a current one — the operator reads
      // a row that says "Fails ACX" while the chapter is queued to be fixed. Marked as
      // working or queued instead, with the old verdict demoted to a footnote.
      if (running) {
        return {
          ...c,
          liveState: c.chapterNumber === job.currentChapter ? "working" : "queued",
        };
      }

      return { ...c, liveState: "idle" };
    });
  }, [report, job]);

  const hasAudio = chapters.length > 0;

  /**
   * What the chapter table says while FOLDED.
   *
   * ⭐ Eighty rows is a long scroll past something that is only sometimes what you came for — but a
   * collapsed header reading just "Chapters" would be opened every time, which puts the scroll straight
   * back. The verdict tally is the reason anyone opens this table, so it goes on the outside.
   */
  const chapterSummary = useMemo(() => {
    if (!chapters.length) return "nothing stitched yet";
    const mastered = chapters.filter((c) => c.mastered);
    const pass = mastered.filter((c) => c.mastered.verdict === "pass").length;
    const fail = mastered.filter((c) => c.mastered.verdict === "fail").length;
    const parts = [`${chapters.length} stitched`, `${mastered.length} mastered`];
    if (mastered.length) parts.push(`${pass} ACX pass`);
    // A failure must never be folded out of sight without saying so.
    if (fail) parts.push(`⚠️ ${fail} fail`);
    return parts.join(" · ");
  }, [chapters]);

  return (
    <Segment className="rs-audio-lab" loading={loading && !report}>
      <Header as="h4" style={{ marginBottom: 4 }}>
        <Icon name="sliders horizontal" />
        <Header.Content>
          Audio Lab
          <Header.Subheader>
            {unitLabel || selector} — measured against ACX, not asserted
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && <Message negative size="tiny" onDismiss={() => setError(null)}>{error}</Message>}

      {/**
        * ⛔ "NO STITCHED AUDIO" USED TO BE THE ONLY THING THIS COULD SAY, and it was wrong often
        * enough to matter. The report matches the selector EXACTLY, so a fully stitched book reports
        * nothing when asked about a different unit — and on 2026-10-01 that message appeared for a
        * book with 44 stitched and mastered chapters sitting under `book.full`.
        *
        * ⭐ `otherSelectors` now comes back on the empty path naming what DOES exist, so the panel can
        * distinguish "nothing has been stitched" from "you are looking at the wrong unit". The first
        * is a to-do; the second is a mis-aimed question, and they need opposite responses.
        */}
      {!hasAudio && !loading && (
        report?.otherSelectors?.length ? (
          <Message warning size="tiny">
            <Icon name="exchange" />
            Nothing stitched for <strong>{unitLabel || selector}</strong> — but this book has audio
            under {report.otherSelectors.length === 1 ? "another unit" : "other units"}:{" "}
            {report.otherSelectors
              .map((o) => `${o.selector} (${o.chapters} chapter${o.chapters === 1 ? "" : "s"})`)
              .join(", ")}
            . Change the scope above to see it — ⚠️ chapters belong to the unit they were stitched as.
          </Message>
        ) : (
          <Message info size="tiny">
            <Icon name="info circle" />
            No stitched audio for this unit yet. Stitch it first — mastering works on the raw
            chapters the stitch produces.
          </Message>
        )
      )}

      {hasAudio && (
        <>
          {/*
            THE READOUT THAT MAKES A RE-STITCH UNNECESSARY. The stitch job is in-memory and
            dies with a server restart; these chapters are durable Neo4j rows plus S3 objects.
            Showing them is the difference between a tool you trust and one you re-run to be
            sure.
          */}
          <Segment vertical style={{ padding: "8px 0" }}>
            <Label.Group size="small">
              <Label>
                <Icon className="loading-icon" name="file audio outline" /> {totals.chapters} chapter
                {totals.chapters === 1 ? "" : "s"} stitched
              </Label>
              <Label>{fmtDuration(totals.durationMs)}</Label>
              <Label>{fmtBytes(totals.audioBytes)}</Label>
              <Label color={totals.mastered ? "blue" : undefined}>
                {totals.mastered}/{totals.chapters} mastered
              </Label>
              <Label color={totals.acxClean === totals.chapters && totals.mastered ? "green" : "grey"}>
                {totals.acxClean} ACX clean
              </Label>
            </Label.Group>
          </Segment>

          <div className="rs-audio-lab-controls">
            <Dropdown
              selection
              compact
              options={presetOptions}
              value={presetKey}
              onChange={(_e, d) => setPresetKey(d.value)}
              disabled={mastering}
            />
            {mastering ? (
              // A long run needs a way out. Abort is the PRIMARY action while one is going —
              // hunting for it behind a disabled "Master" button is the wrong shape when the
              // thing you want is to stop.
              <Button
                negative
                size="small"
                onClick={stopMastering}
              >
                <Icon name="stop" />
                Stop — keeps the {job.chaptersMastered} already done
              </Button>
            ) : (
              <>
                <Button
                  primary
                  size="small"
                  disabled={!hasAudio}
                  onClick={() => runMastering()}
                >
                  <Icon name="magic" />
                  {totals.mastered ? "Re-master" : "Master"} {totals.chapters} chapter
                  {totals.chapters === 1 ? "" : "s"}
                </Button>
                {/**
                  * ⭐ FORCE EXISTS BECAUSE "CURRENT" ONLY MEANS "NEWER THAN ITS RAW".
                  *
                  * `ChapterMasterer.splitAlreadyMastered` decides a chapter is current when
                  * `mastered.createdAt >= raw.createdAt`. Nothing records WHICH preset or which code
                  * produced that master — so when the mastering chain itself changes, every chapter
                  * still looks current and an ordinary Re-master reports *"all 44 chapter(s) were
                  * current, so nothing was re-encoded."*
                  *
                  * That happened on 2026-10-01, when head and tail padding was changed from digital
                  * silence to room tone: the fix was in, the raws were untouched, and there was no way
                  * to apply it from this panel at all. Before this button the only `force: true` in the
                  * UI was "Run anyway" on a halted job.
                  *
                  * ⚠️ Deliberately NOT the primary action. It re-encodes every chapter — tens of
                  * minutes of ffmpeg and a full re-upload — so it stays a secondary control with the
                  * cost named in its tooltip.
                  */}
                {!!totals.mastered && (
                  <Button
                    basic
                    size="small"
                    disabled={!hasAudio}
                    onClick={() => setConfirmForce(true)}
                    title={
                      "Re-encode every chapter even if it looks current. Needed when the mastering "
                      + "chain itself changed — a master is judged only against its raw's timestamp, "
                      + "so a code or preset change leaves every chapter looking up to date."
                    }
                  >
                    <Icon name="redo" />
                    Force all
                  </Button>
                )}
              </>
            )}
            <Button size="small" basic icon="refresh" onClick={load} disabled={loading || mastering} />
          </div>

          {activePreset && (
            <p className="rs-audio-lab-intent">
              {activePreset.intent}
              {!activePreset.acxCompliant && (
                <>
                  {" "}
                  <strong>This preset does not target ACX</strong> — it is the A/B reference and
                  will fail the room-tone checks by design.
                </>
              )}
            </p>
          )}

          {/*
            ⚠️ Provisional values, stated in the UI and not only in a code comment. A BC who
            does not know these are unauthored guesses would reasonably assume they are tuned.
          */}
          <Message size="tiny" warning>
            <Icon name="warning sign" />
            Preset values are <strong>provisional</strong> and have not yet been authored by ear.
            ACX thresholds are recorded from documentation — confirm against ACX before a real
            submission.
          </Message>

          {job && (
            <Message
              size="tiny"
              info={job.status === "running"}
              negative={job.status === "halted" || job.status === "failed"}
              warning={job.status === "aborted"}
              positive={job.status === "complete" && !job.failures.length}
            >
              {job.status === "running" && (
                <>

                  <Icon className="loading-icon" name="circle notch" loading />
                  Mastering with {job.preset.label} — chapter {job.currentChapter ?? "…"} of{" "}
                  {job.chaptersExpected}. {job.chaptersMastered} done,{" "}
                  {/*
                    THE COUNT THAT MATTERS DURING A RUN. "19/19 mastered — 0 ACX clean" is only
                    useful if you see it before the nineteenth. Shown live, a systematic
                    failure is obvious after one or two.
                  */}
                  <strong>{job.chaptersAcxClean} ACX clean</strong>.
                  {/*
                    WHY THE TOTAL MAY NOT BE THE BOOK. A master that died at chapter 62 keeps those
                    62 — they are Neo4j rows and S3 objects, not job state — so the retry does only
                    the remainder. Without this the run says "chapter 3 of 18" for an 80-chapter book
                    and looks like it has lost most of the work.
                  */}
                  {!!job.chaptersSkipped && (
                    <> {job.chaptersSkipped} already mastered at this preset, skipped.</>
                  )}
                </>
              )}

              {job.status === "complete" && (
                <>
                  <Icon name={job.failures.length ? "warning sign" : "check"} />
                  {job.chaptersExpected === 0 ? (
                    <>
                      Already mastered with {job.preset.label} — all {job.chaptersSkipped} chapter(s)
                      were current, so nothing was re-encoded.
                    </>
                  ) : (
                    <>
                      Mastered {job.chaptersMastered}/{job.chaptersExpected} with {job.preset.label} —{" "}
                      {job.chaptersAcxClean} ACX clean.
                      {!!job.chaptersSkipped && (
                        <> {job.chaptersSkipped} were already current and were skipped.</>
                      )}
                    </>
                  )}
                  {!!job.failures.length && (
                    <> Failed: {job.failures.map((f) => f.chapterNumber).join(", ")}.</>
                  )}
                </>
              )}

              {job.status === "aborted" && (
                <>
                  <Icon name="stop circle" />
                  Stopped at your request after {job.chaptersMastered} of {job.chaptersExpected}{" "}
                  chapters. Those {job.chaptersMastered} are mastered and kept — export will use
                  them and fall back to raw for the rest.
                </>
              )}

              {job.status === "halted" && (
                <>
                  <Message.Header>
                    <Icon name="hand paper" /> Stopped itself after{" "}
                    {job.haltReason?.consecutive} chapters failed the same check
                  </Message.Header>
                  <p style={{ marginTop: 6 }}>{job.haltReason?.message}</p>
                  <Button size="mini" onClick={() => runMastering({ force: true })}>
                    <Icon name="play" /> Run anyway
                  </Button>
                </>
              )}

              {job.status === "failed" && (
                <>
                  <Icon name="warning sign" /> Mastering failed: {job.error}
                </>
              )}
            </Message>
          )}

          {/*
            ⭐ THE 80-ROW TABLE FOLDS, AND ITS HEADER STILL ANSWERS THE QUESTION. Eighty chapters is a
            long scroll past something that is only occasionally what you came for — but a collapsed
            header saying just "Chapters" would force it open every time, so the verdict tally rides on
            the outside. Folded, it still says whether the book passes.
          */}
          <CollapsibleSection
            storageKey="rs.audioLab.chapters"
            title="Chapters"
            subtitle="Per-chapter mastering and the ACX verdict. Click a verdict to see the measured values."
            summary={chapterSummary}
          >
          <Table compact="very" size="small" className="rs-audio-lab-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Ch</Table.HeaderCell>
                <Table.HeaderCell>Length</Table.HeaderCell>
                <Table.HeaderCell>Mastered</Table.HeaderCell>
                <Table.HeaderCell>Gain</Table.HeaderCell>
                <Table.HeaderCell>ACX</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">A/B</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {chapters.map((c) => {
                const m = c.mastered;
                const v = m?.verdict ? VERDICT[m.verdict] : null;
                const stale = c.liveState === "queued" || c.liveState === "working";
                // A stale row must not stay expanded showing the previous run's numbers.
                const isOpen = openChapter === c.chapterNumber && !stale;
                const checks = m?.compliance?.checks || [];

                return (
                  <React.Fragment key={c.chapterNumber}>
                    <Table.Row>
                      <Table.Cell>{c.chapterNumber}</Table.Cell>
                      <Table.Cell>{fmtDuration((m || c.raw)?.durationMs)}</Table.Cell>
                      {/* Pending rows carry last run's numbers; dim them so they do not read
                          as this run's result. */}
                      <Table.Cell className={stale ? "rs-stale" : undefined}>
                        {m ? <Label basic>{m.masteringPreset}</Label> : "—"}
                      </Table.Cell>
                      <Table.Cell className={stale ? "rs-stale" : undefined}>
                        {m?.appliedGainDb == null ? "—" : `${m.appliedGainDb > 0 ? "+" : ""}${m.appliedGainDb} dB`}
                      </Table.Cell>
                      <Table.Cell>
                        {/*
                          Three states, not two. A queued chapter's verdict is from the
                          PREVIOUS run — leaving it displayed as-is tells the operator a
                          chapter fails when it is in fact waiting to be re-made.
                        */}
                        {/*
                          EVERY state is a Label of the same shape — working, queued and each
                          verdict. Mixing labels with bare text made rows change height as a
                          run moved down the table, so the thing you were reading shifted under
                          you. Same element, same size, one word swapped.
                        */}
                        {c.liveState === "working" && (
                          <Label color="blue">mastering</Label>
                        )}

                        {c.liveState === "queued" && (
                          <Label basic>
                            <Icon name="clock outline" />
                            queued
                            {v && <span className="rs-muted"> · was {v.text.toLowerCase()}</span>}
                          </Label>
                        )}

                        {c.liveState !== "working" && c.liveState !== "queued" && (
                          v ? (
                            <Label
                              color={v.color}
                              style={{ cursor: checks.length ? "pointer" : "default" }}
                              onClick={() => checks.length && setOpenChapter(isOpen ? null : c.chapterNumber)}
                            >
                              <Icon name={v.icon} />
                              {v.text}
                              {!!checks.length && <Icon name={isOpen ? "caret up" : "caret down"} />}
                            </Label>
                          ) : (
                            <span className="rs-muted">not mastered</span>
                          )
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Popup
                          size="tiny"
                          content={m
                            ? "Compare raw against mastered, switching instantly at the same moment"
                            : "Listen to the raw chapter"}
                          trigger={
                            <Button
                              size="mini"
                              basic
                              icon
                              labelPosition="left"
                              disabled={!c.raw?.uuid}
                              active={pair?.chapterNumber === c.chapterNumber}
                              onClick={() => startCompare(c)}
                            >
                              <Icon name={m ? "exchange" : "play"} />
                              {m ? "A/B" : "Play"}
                            </Button>
                          }
                        />
                      </Table.Cell>
                    </Table.Row>

                    {isOpen && (
                      <Table.Row className="rs-audio-lab-checks">
                        <Table.Cell colSpan={6}>
                          <Table compact="very" size="small" basic="very">
                            <Table.Body>
                              {checks.map((ch) => (
                                <Table.Row key={ch.key}>
                                  <Table.Cell width={4}>{ch.label}</Table.Cell>
                                  <Table.Cell width={3}>
                                    <strong>{fmtValue(ch)}</strong>
                                    {ch.note && <span className="rs-muted"> · {ch.note}</span>}
                                  </Table.Cell>
                                  <Table.Cell width={1}>
                                    {/* null is its own state — unmeasured, not passed. */}
                                    {ch.pass === true && <Icon name="check" color="green" />}
                                    {ch.pass === false && <Icon name="times" color="red" />}
                                    {ch.pass == null && <Icon name="question" color="yellow" />}
                                  </Table.Cell>
                                  <Table.Cell className="rs-muted">
                                    target {fmtTarget(ch)}
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table>
          </CollapsibleSection>

          {pair && (
            <Segment className="rs-ab" vertical>
              <div className="rs-ab-row">
                <Button icon primary circular onClick={togglePlay}>
                  <Icon name={playing ? "pause" : "play"} />
                </Button>

                <Button.Group size="small">
                  <Button
                    active={side === "raw"}
                    onClick={() => flipSide("raw")}
                    disabled={!pair.masteredUrl}
                  >
                    A · Raw
                  </Button>
                  <Button.Or text="vs" />
                  <Button
                    active={side === "mastered"}
                    onClick={() => flipSide("mastered")}
                    disabled={!pair.masteredUrl}
                  >
                    B · Mastered
                  </Button>
                </Button.Group>

                <span className="rs-ab-label">ch {pair.chapterNumber}</span>

                {pair.masteredUrl && (
                  <Popup
                    size="tiny"
                    wide
                    content={
                      "Mastering changes loudness on purpose, and louder almost always sounds " +
                      "better — so an unmatched comparison mostly measures the gain. Matched, " +
                      "you are judging the limiter and the room tone instead."
                    }
                    trigger={
                      <Button
                        size="small"
                        basic={!levelMatch}
                        color={levelMatch ? "teal" : undefined}
                        onClick={() => setLevelMatch((v) => !v)}
                      >
                        <Icon name="balance scale" />
                        Level-matched
                        {pair.deltaDb ? ` (${pair.deltaDb > 0 ? "+" : ""}${pair.deltaDb.toFixed(1)} dB)` : ""}
                      </Button>
                    }
                  />
                )}
              </div>

              {!pair.masteredUrl && (
                <p className="rs-muted" style={{ marginTop: 6 }}>
                  Nothing to compare against yet — this chapter has not been mastered.
                </p>
              )}

              {/*
                The RAW element carries the transport. Both play together with one muted, so a
                switch is instantaneous rather than a reload; scrubbing here moves the pair.
              */}
              <audio
                ref={rawRef}
                src={pair.rawUrl}
                controls
                preload="auto"
                style={{ width: "100%", marginTop: 8 }}
                onPlay={() => { setPlaying(true); syncB(true); }}
                onPause={() => { setPlaying(false); syncB(false); }}
                onSeeked={() => syncB(!rawRef.current?.paused)}
                onRateChange={() => {
                  const b = masteredRef.current;
                  if (b && rawRef.current) b.playbackRate = rawRef.current.playbackRate;
                }}
                // Long chapters drift; a periodic nudge keeps the two within a breath of each
                // other so a switch never lands half a word out.
                onTimeUpdate={() => syncB(!rawRef.current?.paused)}
                onEnded={() => { setPlaying(false); syncB(false); }}
              />
              {pair.masteredUrl && (
                <audio ref={masteredRef} src={pair.masteredUrl} preload="auto" />
              )}
            </Segment>
          )}
        </>
      )}

      {/**
        * ⛔ GUARDED ON `confirmForce`, not rendered unconditionally. `totals` is `report?.totals`, so
        * it is undefined until the report loads — and every other use of it in this file sits inside
        * the loaded-data branch. Mounting this modal outside that branch read `totals.chapters` on
        * first render and threw "Cannot read properties of undefined". Optional chaining below as
        * well, since a report can also arrive without totals.
        */}
      {confirmForce && (
      <ConfirmModal
        open={confirmForce}
        title="Re-encode every chapter?"
        icon="redo"
        danger={false}
        confirmLabel="Force re-master"
        cancelLabel="Cancel"
        loading={mastering}
        note={
          "A master is judged only against its raw audio's timestamp, so a change to the mastering "
          + "chain itself leaves every chapter looking current. Force is how you apply one."
        }
        onConfirm={() => { setConfirmForce(false); runMastering({ redo: true }); }}
        onCancel={() => setConfirmForce(false)}
      >
        <p>
          This re-encodes all {totals?.chapters ?? 0} chapter
          {totals?.chapters === 1 ? "" : "s"} with{" "}
          <strong>{activePreset?.label || presetKey}</strong>, even the ones reported as current —
          tens of minutes of processing and a full re-upload.
        </p>
        <p style={{ marginBottom: 0 }}>
          It costs no ElevenLabs operations or characters. Existing raw audio is untouched, and the
          previous masters are superseded rather than deleted.
        </p>
      </ConfirmModal>
      )}
    </Segment>
  );
}
